<template>
	<div class="auth-shell">
		<!-- LEFT — editorial panel (desktop only) -->
		<aside class="auth-shell__left">
			<div class="auth-shell__bloom-1" />
			<div class="auth-shell__bloom-2" />

			<div class="auth-shell__top">
				<div class="auth-shell__eyebrow">
					<span class="auth-shell__eyebrow-line" />
					<span class="auth-shell__eyebrow-text"><slot name="eyebrow">Sign in to platform</slot></span>
				</div>
			</div>

			<div class="auth-shell__center">
				<h2 class="auth-shell__headline">
					<slot name="headline">Welcome back<br/>to <span class="accent">{{ appName }}.</span></slot>
				</h2>
				<p class="auth-shell__lead">
					<slot name="lead">Sign in to manage your DOOH campaigns, screens and real-time analytics.</slot>
				</p>
			</div>

			<div class="auth-shell__footer">
				<p class="auth-shell__copy">&copy; {{ currentYear }} {{ appName }}</p>
				<div class="auth-shell__status">
					<span class="auth-shell__status-dot" />
					All systems operational
				</div>
			</div>
		</aside>

		<!-- RIGHT — form panel -->
		<main class="auth-shell__right">
			<p class="auth-shell__mobile-brand">{{ appName }}</p>
			<div class="auth-card">
				<slot />
			</div>
		</main>
	</div>
</template>


<script>
export default {
	name: 'AuthLayout',

	computed: {
		appName() {
			return (this.$uioverrides && this.$uioverrides.name) || 'DISPL Ads';
		},
		currentYear() {
			return new Date().getFullYear();
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$bg: $v2-bg
$card: $v2-card-bg
$font: $v2-font

.auth-shell
	min-height: 100vh
	display: flex
	background: $bg
	font-family: $font

// ── Left panel ─────────────────────
.auth-shell__left
	display: none
	flex: 1
	flex-direction: column
	justify-content: space-between
	padding: 64px 64px 28px
	position: relative
	overflow: hidden
	background: linear-gradient(135deg, rgba($teal, 0.06) 0%, $bg 50%, rgba($purple, 0.05) 100%)

	@media (min-width: 1024px)
		display: flex

	&::before
		content: ''
		position: absolute
		inset: 0
		background-image: radial-gradient(circle at 1px 1px, rgba($text, 0.06) 1px, transparent 0)
		background-size: 22px 22px
		opacity: 0.6
		mask-image: radial-gradient(ellipse 90% 70% at 30% 50%, black, transparent 80%)
		pointer-events: none

.auth-shell__bloom-1,
.auth-shell__bloom-2
	position: absolute
	border-radius: 50%
	filter: blur(64px)
	pointer-events: none

.auth-shell__bloom-1
	top: -128px
	left: -96px
	width: 420px
	height: 420px
	background: rgba($teal, 0.10)

.auth-shell__bloom-2
	bottom: -128px
	right: 0
	width: 360px
	height: 360px
	background: rgba($purple, 0.10)

.auth-shell__top
	position: relative
	z-index: 1

.auth-shell__eyebrow
	display: inline-flex
	align-items: center
	gap: 12px

.auth-shell__eyebrow-line
	width: 40px
	height: 1px
	background: $teal

.auth-shell__eyebrow-text
	font-size: 11px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.22em
	color: $teal

.auth-shell__center
	position: relative
	z-index: 1
	max-width: 36rem

.auth-shell__headline
	font-size: 44px
	font-weight: 700
	line-height: 1
	letter-spacing: -0.03em
	color: $text

	.accent
		color: $teal

	@media (min-width: 1280px)
		font-size: 54px

.auth-shell__lead
	margin-top: 24px
	font-size: 15px
	line-height: 1.6
	color: $muted
	max-width: 28rem

.auth-shell__footer
	position: relative
	z-index: 1
	display: flex
	align-items: flex-end
	justify-content: space-between

.auth-shell__copy
	font-size: 11px
	font-weight: 500
	text-transform: uppercase
	letter-spacing: 0.16em
	color: $muted

.auth-shell__status
	display: flex
	align-items: center
	gap: 8px
	font-size: 11px
	color: $muted

.auth-shell__status-dot
	position: relative
	display: inline-flex
	height: 8px
	width: 8px

	&::before,
	&::after
		content: ''
		position: absolute
		inset: 0
		border-radius: 50%
		background: $v2-green

	&::before
		opacity: 0.6
		animation: ping 1.6s cubic-bezier(0,0,0.2,1) infinite

@keyframes ping
	75%, 100%
		transform: scale(2)
		opacity: 0

// ── Right panel ────────────────────
.auth-shell__right
	flex: 1
	display: flex
	align-items: center
	justify-content: center
	padding: 24px
	position: relative

	@media (min-width: 1024px)
		flex: none
		width: 520px
		padding: 40px

.auth-shell__mobile-brand
	position: absolute
	top: 24px
	left: 24px
	font-size: 14px
	font-weight: 700
	color: $text
	letter-spacing: -0.01em

	@media (min-width: 1024px)
		display: none

// ── Card ───────────────────────────
.auth-card
	width: 100%
	max-width: 420px
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: 24px
	box-shadow: 0 24px 64px -24px rgba($text, 0.18)
	padding: 32px
	position: relative
	box-sizing: border-box
</style>
